<template>
  <section class="activity-log-container">
    <el-tabs
      v-model="activeTab"
      :destroy-on-hide="true"
      @tab-click="handleTabClick"
    >
      <el-tab-pane label="Audit Logs" name="auditLogs">
        <div class="filter-controls">
          <el-row :gutter="20" class="filter-row">
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                @change="handleFilterChange"
                class="date-picker"
                :picker-options="pickerOptions"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-select
                v-model="userFilter"
                placeholder="Filter by user"
                clearable
                @change="handleFilterChange"
                class="filter-select"
              >
                <el-option
                  v-for="user in uniqueUsers()"
                  :key="user.id"
                  :label="user.name"
                  :value="user.id"
                />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-select
                v-model="actionTypeFilter"
                placeholder="Filter by action"
                @change="handleFilterChange"
                clearable
                class="filter-select"
              >
                <el-option label="Create" value="create" />
                <el-option label="Update" value="update" />
                <el-option label="Delete" value="delete" />
                <el-option
                  label="Send Notification"
                  value="send_notification"
                />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6" :lg="6">
              <el-select
                v-model="source"
                placeholder="Filter by source"
                clearable
                @change="handleFilterChange"
                class="filter-select"
              >
                <el-option
                  v-for="user in sourceTypes"
                  :key="user"
                  :label="user"
                  :value="user"
                />
              </el-select>
            </el-col>
          </el-row>
        </div>
        <div class="table-container">
          <el-table
            :data="loading ? Array(pageSize).fill({}) : logs"
            style="width: 100%"
            :total="totalLogs"
            @row-click="handleRowClick"
            border
            max-height="530"
            :remote="true"
            :default-sort="{ prop: sortField, order: sortOrder }"
            @sort-change="handleSortChange"
            stripe
            class="activity-table"
            :row-class-name="tableRowClassName"
          >
            <el-table-column label="SL No" width="80" align="center" fixed>
              <template #default="{ $index }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 100%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  {{ getSerialNumber($index) }}
                </template>
              </template>
            </el-table-column>
            <el-table-column
              prop="created_at"
              label="Date/Time"
              sortable="custom"
              :width="isMobile ? 120 : 180"
              :class-name="isMobile ? 'mobile-column' : ''"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 100%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <div class="timestamp-cell">
                    {{ formatDateTime(row.created_at) }}
                  </div>
                </template>
              </template>
            </el-table-column>

            <el-table-column
              prop="record"
              label="Record"
              :width="isMobile ? 180 : 200"
              :class-name="isMobile ? 'mobile-column' : ''"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 90%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <router-link
                    v-if="row.record_link"
                    :to="row.record_link"
                    class="record-link"
                  >
                    {{ row.record_name }}
                  </router-link>
                  <span v-else class="record-text">{{ row.record }}</span>
                </template>
              </template>
            </el-table-column>

            <el-table-column
              prop="activity_by"
              label="User"
              :width="isMobile ? 200 : 250"
              :class-name="isMobile ? 'mobile-column' : ''"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 100%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <div class="user-cell" v-if="row.activity_by">
                    <el-avatar size="small" class="user-avatar">
                      {{ getInitials(row.activity_by) }}
                    </el-avatar>
                    <span class="user-name" :class="{ truncate: isMobile }">
                      {{
                        isMobile
                          ? getShortName(row.activity_by)
                          : row.activity_by
                      }}
                    </span>
                  </div>
                </template>
              </template>
            </el-table-column>

            <el-table-column
              prop="summary"
              label="Changes"
              :class-name="isMobile ? 'mobile-column' : ''"
              :min-width="isMobile ? 200 : 300"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <div style="width: 100%">
                    <el-skeleton :animated="true" style="width: 100%">
                      <template slot="template">
                        <el-skeleton-item
                          variant="text"
                          style="width: 100%; height: 16px; margin-bottom: 8px"
                        />
                        <el-skeleton-item
                          variant="text"
                          style="width: 80%; height: 16px"
                        />
                      </template>
                    </el-skeleton>
                  </div>
                </template>
                <template v-else>
                  <div class="changes-summary">
                    <div
                      v-for="(change, idx) in row.content"
                      :key="idx"
                      class="change-item"
                    >
                      <p v-html="change"></p>
                    </div>
                  </div>
                </template>
              </template>
            </el-table-column>
            <el-table-column
              prop="action"
              label="Action"
              :width="isMobile ? 100 : 130"
              :class-name="isMobile ? 'mobile-column' : ''"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 80%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <el-tag
                    :type="getActionTagType(row.action)"
                    size="small"
                    class="action-tag"
                    effect="dark"
                  >
                    {{ isMobile ? getShortAction(row.action) : row.action }}
                  </el-tag>
                </template>
              </template>
            </el-table-column>
            <el-table-column
              prop="action_from"
              label="Action via"
              :width="isMobile ? 100 : 150"
              :class-name="isMobile ? 'mobile-column' : ''"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template slot="template">
                      <el-skeleton-item
                        variant="text"
                        style="width: 70%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <el-tag
                    :type="
                      row.action_from === 'Automation' ? 'warning' : 'info'
                    "
                    size="small"
                    class="source-tag"
                    effect="light"
                  >
                    <i
                      v-if="!isMobile"
                      :class="getSourceIcon(row.action_from)"
                      class="source-icon"
                    ></i>
                    {{
                      isMobile
                        ? getShortSource(row.action_from)
                        : row.action_from
                    }}
                  </el-tag>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="pagination-wrapper">
          <el-pagination
            :class="'custom-pagination'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page.sync="currentPage"
            :page-sizes="paginationProps"
            :page-size="pageSize"
            :pager-count="isMobile ? 3 : 5"
            layout="prev, pager, next, jumper, sizes, total"
            :total="totalLogs"
            background
          ></el-pagination>
        </div>
      </el-tab-pane>
      <el-tab-pane label="Automation Logs" name="automationLogs">
        <p>
          This section will show logs for automated processes and bulk
          operations.
        </p>
        <div class="filter-controls">
          <el-row :gutter="20" class="filter-row">
            <el-col :xs="24" :sm="12" :md="8" :lg="8">
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                @change="handleFilterChange"
                class="date-picker"
                :picker-options="pickerOptions"
              />
            </el-col>
            <el-col :xs="24" :sm="12" :md="7" :lg="7">
              <el-select
                v-model="entityActionsArray"
                placeholder="Filter by automation"
                @change="handleFilterChange"
                clearable
                class="filter-select"
              >
                <el-option
                  v-for="item in entityActions"
                  :key="item._id"
                  :label="item.name"
                  :value="item._id"
                ></el-option>
              </el-select>
            </el-col>
          </el-row>
        </div>
        <div class="table-container">
          <el-table
            :data="loading ? Array(pageSize).fill({}) : automationLogs"
            style="width: 100%"
            :total="totalAutomationLogs"
            border
            max-height="530"
            :remote="true"
            :default-sort="{ prop: sortField, order: sortOrder }"
            @sort-change="handleSortChange"
            stripe
            class="activity-table"
            :row-class-name="tableRowClassName"
          >
            <el-table-column label="SL No" width="80" align="center" fixed>
              <template #default="{ $index }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template #template>
                      <el-skeleton-item
                        variant="text"
                        style="width: 100%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  {{ getSerialNumber($index) }}
                </template>
              </template>
            </el-table-column>

            <!-- Date/Time -->
            <el-table-column
              prop="created_at"
              label="Date/Time"
              sortable="custom"
              :width="isMobile ? 120 : 180"
            >
              <template #default="{ row }">
                <template v-if="loading">
                  <el-skeleton :animated="true" style="width: 100%">
                    <template #template>
                      <el-skeleton-item
                        variant="text"
                        style="width: 100%; height: 20px"
                      />
                    </template>
                  </el-skeleton>
                </template>
                <template v-else>
                  <div class="timestamp-cell">
                    {{ formatDateTime(row.created_at) }}
                  </div>
                </template>
              </template>
            </el-table-column>
            <el-table-column
              prop="action_type"
              label="Action Type"
              :width="isMobile ? 120 : 160"
            >
              <template #default="{ row }">
                <el-tag type="info" size="small">{{ row.action_type }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="status"
              label="Status"
              :width="isMobile ? 100 : 130"
            >
              <template #default="{ row }">
                <el-tag
                  :type="getTagType(row.action_status)"
                  size="small"
                  effect="dark"
                >
                  {{ row.action_status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="error_message"
              label="Message"
              :min-width="isMobile ? 150 : 250"
            >
              <template #default="{ row }">
                <span v-if="row.error_message">{{ row.error_message }}</span>
                <span v-else>-</span>
              </template>
            </el-table-column>

            <el-table-column label="View Details" :min-width="120">
              <template #default="{ row }">
                <el-button
                  size="mini"
                  class="el-dropdown-link px-2"
                  @click="openDetailsModal(row.details)"
                  :disabled="!row.details"
                >
                  <span class="actionsStyle"> View Details </span>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="pagination-wrapper">
          <el-pagination
            :class="'custom-pagination'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page.sync="currentPage"
            :page-sizes="paginationProps"
            :page-size="pageSize"
            :pager-count="isMobile ? 3 : 5"
            layout="prev, pager, next, jumper, sizes, total"
            :total="totalAutomationLogs"
            background
          ></el-pagination>
        </div>
      </el-tab-pane>
    </el-tabs>
    <el-drawer
      title="Activity Logs"
      :visible.sync="showSingleDataLogs"
      :direction="'rtl'"
      class="layout-drawer"
      :with-header="true"
      :modal="true"
      :append-to-body="true"
      :z-index="3000"
      :size="getIsMobile ? '80%' : '40%'"
      :before-close="handleLogsBeforeClose"
    >
      <AuditLogs
        v-if="showSingleDataLogs"
        :fieldsObject="fieldsObject"
        :createdAtData="selectedData"
        :dataLabel="selectedDataLabel"
      ></AuditLogs>
    </el-drawer>
    <dialog-component
      :title="'Record Details'"
      :visible="detailsModalVisible"
      @before-close="closeDetailsModal"
      :containerWidth="'40%'"
      :containerMaxHeight="'70%'"
      :containerMinHeight="'30%'"
      :isShowFooter="false"
    >
      <div v-if="this.selectedDetails" class="record-dialog">
        <div
          v-for="(value, key) in selectedDetails"
          :key="key"
          class="detail-item"
        >
          <p>
            <strong>{{ formatKey(key) }}: </strong>
            <span v-html="formatValue(value)"></span>
          </p>
        </div>
      </div>
    </dialog-component>
  </section>
</template>

<script>
import moment from "moment";
import { postAPICall } from "../helpers/httpHelper";
import { fetchEntityById } from "../repo/entityRepo";
import AutomationHelper from "../mixins/AutomationHelper";
import templateBuilderHelper from "../mixins/templateBuilderHelper";
import FieldsFormatHelper from "../mixins/FieldsFormatHelper";
import { mapGetters } from "vuex";

const ACTION_TAG_TYPES = {
  created: "success",
  updated: "primary",
  deleted: "danger",
  send_notification: "warning",
  default: "info",
};

export default {
  name: "GlobalActivityLogs",
  mixins: [AutomationHelper, templateBuilderHelper, FieldsFormatHelper],

  props: {
    entity: {
      type: Object,
      default: null,
    },
    entityId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      entity_id: "",
      loading: false,
      showSingleDataLogs: false,
      selectedData: null,
      selectedDataLabel: null,
      pickerOptions: {
        disabledDate(date) {
          return date.getTime() >= Date.now();
        },
      },
      dataObject: {},
      logs: [],
      userFilter: "",
      source: "",
      actionTypeFilter: "",
      sourceTypes: [
        "Entity",
        "Automation",
        "OAuth",
        "Bulk Update",
        "Form Builders",
        "Bulk Delete",
      ],
      fieldsObject: {},
      dateRange: [],
      currentPage: 1,
      pageSize: 5,
      totalLogs: 0,
      sortField: "timestamp",
      sortOrder: "descending",
      currentEntity: null,
      allEntityFields: [],
      windowWidth: window.innerWidth,
      activeTab: "auditLogs",
      detailsModalVisible: false,
      selectedDetails: null,
      automationActionFilter: null,
      entitySelected: null,
      statusFilter: null,
      entityActionsArray: [],
      totalAutomationLogs: 0,
      entityActions: [],
      automationLogs: [],
    };
  },
  components: {
    AuditLogs: () => import("@/components/AuditLogs.vue"),
  },
  computed: {
    paginationProps() {
      return this.isMobile ? [5, 10] : [5, 10, 20, 50, 100];
    },

    isMobile() {
      return this.getIsMobile;
    },
    ...mapGetters("navigationOpen", ["getIsMobile"]),
    ...mapGetters("automationPipeline", ["getEntityActions"]),
  },

  methods: {
    formatKey(key) {
      return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatValue(value) {
      if (!value) return "";
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const formatted = String(value).replace(urlRegex, (url) => {
        let displayUrl = url;
        if (url.length > 50) {
          displayUrl =
            url.substring(0, 30) + "..." + url.substring(url.length - 10);
        }
        return `<a href="${url}" target="_blank" style="color:#409EFF; text-decoration:underline;">${displayUrl}</a>`;
      });
      return formatted;
    },
    getTagType(status) {
      if (status === "success") {
        return "success";
      } else {
        return "danger";
      }
    },
    handleTabClick(tab) {
      this.activeTab = tab.name;
      if (tab.name === "auditLogs") {
        this.automationActionFilter = "";
        this.statusFilter = "";
        this.dateRange = [];
        this.source = "";
        this.fetchGlobalLogs();
      } else if (tab.name === "automationLogs") {
        this.userFilter = "";
        this.actionTypeFilter = "";
        this.dateRange = [];
        this.source = "";
        this.fetchAutomationLogs();
      }
    },
    async fetchAutomationLogs() {
      try {
        this.loading = true;
        const params = {
          page: this.currentPage,
          limit: this.pageSize,
          sort: this.sortField,
          sortOrder: this.sortOrder == "ascending" ? "asc" : "desc",
          actionType: this.automationActionFilter,
          actionStatus: this.statusFilter,
          entityActions: this.entityActionsArray,
          ...this.getDateRangeParams(),
        };
        const response = await postAPICall(
          "GET",
          `/audit/fetch-automation-logs?entityId=${
            this.entity_id
          }&${this.buildQueryParams(params)}`
        );
        this.automationLogs = response.data;
        this.totalAutomationLogs = response.total;
        await this.enrichLogData();
      } catch (error) {
        console.error("Error fetching global logs:", error);
      } finally {
        this.loading = false;
      }
    },
    openDetailsModal(details) {
      this.selectedDetails = details;
      this.detailsModalVisible = true;
    },
    closeDetailsModal() {
      this.detailsModalVisible = false;
      this.selectedDetails = null;
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    getSerialNumber(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    handleRowClick(row) {
      let currentData = this.dataObject[row.entity_data_id];
      if (!currentData) {
        console.warn("No data found for entity_data_id:", row.entity_data_id);
        return;
      }
      this.selectedData = {
        created_at: currentData.created_at,
        action_by: currentData.created_by,
        is_create: true,
        _id: row.entity_data_id,
      };
      this.selectedDataLabel = row.record;
      this.showSingleDataLogs = true;
    },

    getShortName(name) {
      if (!name) return "";
      if (name.length > 12) {
        return name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase();
      }
      return name;
    },
    handleLogsBeforeClose() {
      this.showSingleDataLogs = false;
    },

    getShortAction(action) {
      if (!action) return "";
      if (action.length > 8) {
        return action.substring(0, 3);
      }
      return action;
    },

    getShortSource(source) {
      if (!source) return "";
      if (source.length > 8) {
        return source
          .split(" ")
          .map((s) => s[0])
          .join("");
      }
      return source;
    },

    tableRowClassName() {
      return this.isMobile ? "mobile-row" : "";
    },

    getInitials(name) {
      if (!name) return "";
      const parts = name.split(" ");
      return parts
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },

    getSourceIcon(source) {
      switch (source) {
        case "Automation":
          return "el-icon-cpu";
        case "Entity":
          return "el-icon-user";
        case "OAuth":
          return "el-icon-key";
        case "Bulk Update":
          return "el-icon-document-copy";
        case "Bulk Delete":
          return "el-icon-delete";
        case "Form Builders":
          return "el-icon-document";
        default:
          return "el-icon-question";
      }
    },
    uniqueUsers() {
      const users = new Map();
      this.logs.forEach((log) => {
        if (log.activity_by) {
          users.set(log.action_by, {
            id: log.action_by,
            name: log.activity_by,
          });
        }
      });
      return Array.from(users.values());
    },
    async fetchGlobalLogs() {
      try {
        this.loading = true;

        const params = {
          page: this.currentPage,
          limit: this.pageSize,
          sort: this.sortField,
          sortOrder: this.sortOrder == "ascending" ? "asc" : "desc",
          actionType: this.actionTypeFilter,
          userId: this.userFilter,
          actionFrom: this.source,
          ...this.getDateRangeParams(),
        };
        const response = await postAPICall(
          "GET",
          `/audit/fetch-logs?entityId=${this.entity_id}&${this.buildQueryParams(
            params
          )}`
        );

        this.logs = response.data;
        this.totalLogs = response.total;
        await this.enrichLogData();
      } catch (error) {
        console.error("Error fetching global logs:", error);
        this.$message.error("Failed to fetch activity logs");
      } finally {
        this.loading = false;
      }
    },

    async enrichLogData() {
      const [userIds, recordIds] = this.extractIdsFromLogs();

      const [usersResponse, entitiesData] = await Promise.all([
        this.fetchUserInfo(userIds),
        this.fetchEntitiesData(recordIds),
      ]);

      this.enrichLogsWithAdditionalData(usersResponse, entitiesData);
    },

    extractIdsFromLogs() {
      const userIds = new Set();
      const recordIds = new Set();

      this.logs.forEach((log) => {
        if (log.action_by) userIds.add(log.action_by);
        if (log.entity_data_id) recordIds.add(log.entity_data_id);
      });

      return [Array.from(userIds), Array.from(recordIds)];
    },

    async fetchUserInfo(userIds) {
      if (!userIds.length) return { data: {} };
      return postAPICall("POST", "/entities-data/users-info", {
        user_ids: userIds,
      });
    },

    async fetchEntitiesData(dataIds) {
      if (!dataIds.length) return { data: [] };

      const templateFieldsMap = this.buildTemplateFieldsMap();
      const template_fields_data = Object.entries(templateFieldsMap).map(
        ([template_id, fields]) => ({
          template_id,
          fields,
        })
      );

      const params = {
        entity_id: this.entity_id,
        entity_data_ids: dataIds,
        limit: this.pageSize,
        is_from_logs: true,
        page: 1,
        template_fields_data,
      };

      const response = await postAPICall(
        "POST",
        "/entities-data/entity/data/v2",
        params
      );
      return response?.data || [];
    },

    buildTemplateFieldsMap() {
      const templateKeys = (this.currentEntity.primaryFields || []).map(
        (e) => `${e.template_id}#${e.key}`
      );
      const fields = this.allEntityFields.filter((e) =>
        templateKeys.includes(e.key)
      );

      return fields.reduce((acc, field) => {
        const [templateId, key] = field.key.split("#");
        if (!acc[templateId]) acc[templateId] = [];

        if (field.inputType === "ENTITY") {
          acc[templateId].push(key, `${key}/name`);
        } else {
          acc[templateId].push(key);
        }

        return acc;
      }, {});
    },

    enrichLogsWithAdditionalData(usersResponse, entitiesData) {
      const usersMap = new Map(
        Object.entries(usersResponse.data || {}).map(([id, user]) => [
          id,
          `${user.first_name} ${user.last_name}`,
        ])
      );

      this.dataObject = (entitiesData.data || []).reduce((acc, e) => {
        acc[e._id] = e;
        return acc;
      }, {});

      this.logs.forEach((log, index) => {
        if (log.action_by && usersMap.has(log.action_by)) {
          log.activity_by = usersMap.get(log.action_by);
        }

        log.record = this.getLabel(
          this.dataObject[log.entity_data_id]?.templates_fields_data,
          this.currentEntity,
          index
        );
        switch (log.action) {
          case "Created":
            log.content = [
              `<p><i>${log.activity_by}</i> created this record.</p>`,
            ];
            break;
          case "Updated":
            log.content = this.prepareContent(
              log.differences || {},
              this.fieldsObject
            );
            break;
          case "Deleted":
            // log.content = [
            //   `<p>The record has been deleted by <i>${log.activity_by}</i></p>`,
            // ];
            log.content = [
              `<p>The record has been deleted by <i>${log.activity_by}</i></p>`,
              ...(log.additional_data?.delete_reason
                ? [`<p><b>Reason</b>: ${log.additional_data.delete_reason}</p>`]
                : []),
            ];
            break;
          case "Send Notification":
            log.content = [
              `<p>A notification has been sent by <i>${log.activity_by}</i></p>`,
            ];
            break;
        }
      });
    },

    getDateRangeParams() {
      if (!this.dateRange || this.dateRange.length !== 2) return {};

      return {
        startDate: moment(this.dateRange[0]).startOf("day").toISOString(),
        endDate: moment(this.dateRange[1]).endOf("day").toISOString(),
      };
    },

    buildQueryParams(params) {
      return Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== "")
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
    },

    formatDateTime(timestamp) {
      return this.$options.filters.getFormattedDateTypeThree(timestamp);
    },

    getActionTagType(actionType) {
      if (!actionType) return ACTION_TAG_TYPES.default;
      const key = actionType.toLowerCase().trim().replace(/\s+/g, "_");
      return ACTION_TAG_TYPES[key] || ACTION_TAG_TYPES.default;
    },

    getLabel(item, selectedEntity = this.currentEntity, index = 1) {
      if (!item) {
        return "-";
      }
      if (!selectedEntity?.primaryFields) return `Data ${index}`;

      const name = selectedEntity.primaryFields
        .map((primaryField) => {
          const key = `${primaryField.template_id}#${primaryField.key}`;
          const field = this.fieldsObject[key] || primaryField;

          return field.input_type === "ENTITY"
            ? item[`${key}/name`]
            : item[key];
        })
        .filter(Boolean);

      return name.length ? name.join(" - ") : `Data ${index}`;
    },

    handleFilterChange() {
      this.currentPage = 1;
      if (this.activeTab === "auditLogs") {
        this.fetchGlobalLogs();
      } else if (this.activeTab === "automationLogs") {
        this.fetchAutomationLogs();
      }
    },

    handleSortChange({ prop, order }) {
      this.sortField = prop;
      this.sortOrder = order;
      if (this.activeTab === "auditLogs") {
        this.fetchGlobalLogs();
      } else if (this.activeTab === "automationLogs") {
        this.fetchAutomationLogs();
      }
    },

    handleSizeChange(size) {
      this.currentPage = 1;
      this.pageSize = size;
      if (this.activeTab === "auditLogs") {
        this.fetchGlobalLogs();
      } else if (this.activeTab === "automationLogs") {
        this.fetchAutomationLogs();
      }
    },

    handleCurrentChange(page) {
      this.currentPage = page;
     if (this.activeTab === "auditLogs") {
        this.fetchGlobalLogs();
      } else if (this.activeTab === "automationLogs") {
        this.fetchAutomationLogs();
      }
    },
    async fetchEntityActions() {
      this.loading = true;
      let params = {
        limit: this.pageSize > 0 ? this.pageSize : 10,
        page: this.page,
        entity_id: this.entityId,
      };
      if (this.order_by) {
        params.order_by = this.order_by;
      }
      if (this.order_type) {
        params.order_type = this.order_type;
      }
      if (this.search_string) {
        params["search_string"] = this.search_string;
      }
      if (this.selectedType) {
        params["status"] = this.selectedType;
      }
      await this.$store.dispatch("automationPipeline/getEntityActions", params);
      if (this.getEntityActions) {
        this.entityActions = this.getEntityActions.data || [];
        this.total = this.getEntityActions.total || 0;
      }
      this.loading = false;
    },
    async initializeComponent() {
      this.currentEntity =
        this.entity || (await fetchEntityById(this.entity_id, false));
      this.allEntityFields = this.getAllFieldsFromEntity(
        this.currentEntity,
        false,
        false,
        true
      );

      this.fieldsObject = this.allEntityFields.reduce((acc, field) => {
        acc[field.key] = field;
        return acc;
      }, {});

      await this.fetchGlobalLogs();
    },

    // ... rest of your existing methods ...
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.entity_id = this.entityId || this.$route.params.entityId;
    this.initializeComponent();
    this.fetchEntityActions();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped lang="scss">
.activity-log-container {
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  @media (min-width: 768px) {
    padding: 24px;
  }
}

.filter-controls {
  margin-bottom: 16px;
  padding: 12px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @media (min-width: 768px) {
    margin-bottom: 24px;
    padding: 16px;
  }

  .filter-row {
    margin-bottom: -16px !important;
    cursor: pointer;

    .el-col {
      margin-bottom: 16px;
    }
  }

  .filter-select,
  .date-picker {
    width: 100%;

    ::v-deep .el-input__inner {
      border-radius: 6px;
      border-color: #e2e8f0;
      &:hover {
        border-color: #cbd5e0;
      }
    }
  }
}

.table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.activity-table {
  min-width: 800px;

  @media (min-width: 768px) {
    min-width: auto;
  }

  ::v-deep .el-table__header {
    th {
      background-color: #f1f5f9;
      color: #334155;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  ::v-deep .el-table__body {
    tr:hover td {
      background-color: #f8fafc !important;
    }

    .mobile-row {
      td {
        padding: 8px 0;
      }
    }

    .mobile-column {
      .cell {
        padding-left: 4px;
        padding-right: 4px;
      }
    }
  }

  .timestamp-cell {
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  }

  .record-link {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;

    &:hover {
      color: #2563eb;
      text-decoration: underline;
    }
  }

  .record-text {
    color: #334155;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }

  .user-cell {
    display: flex;
    align-items: center;
    gap: 8px;

    .user-avatar {
      background-color: #e2e8f0;
      color: #475569;
      font-weight: 600;
      flex-shrink: 0;
    }

    .user-name {
      color: #334155;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.truncate {
        max-width: 60px;
      }
    }
  }

  .action-tag {
    font-weight: 500;
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  .source-tag {
    font-weight: 500;
    white-space: nowrap;

    .source-icon {
      margin-right: 4px;
    }
  }

  .changes-summary {
    cursor: pointer;
    max-height: 150px;
    overflow-y: auto;
    padding-right: 8px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 3px;
    }

    .change-item {
      margin-bottom: 6px;
      line-height: 1.5;
      font-size: 13px;
      color: #475569;

      &:last-child {
        margin-bottom: 0;
      }

      p {
        margin: 0;
        word-break: break-word;
      }
    }
  }
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  @media (min-width: 768px) {
    margin-top: 24px;
  }

  ::v-deep .el-pagination {
    white-space: nowrap;

    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 32px;
      height: 32px;
      line-height: 32px;
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      color: #64748b;
      margin: 0 2px;

      &:hover {
        color: #3b82f6;
      }
    }

    .el-pager li.active {
      background: #3b82f6;
      color: white;
      border-color: #3b82f6;
    }

    .el-pagination__total {
      color: #64748b;
      margin-right: 8px;
    }

    .el-pagination__jump {
      color: #64748b;
      margin-left: 8px;

      .el-input {
        width: 50px;

        .el-input__inner {
          height: 32px;
          border-color: #e2e8f0;
          border-radius: 6px;
        }
      }
    }

    @media (max-width: 767px) {
      .el-pagination__sizes,
      .el-pagination__jump {
        display: none;
      }
    }
  }
}

/* Skeleton loading animation */
.el-skeleton {
  line-height: 1;

  &__item {
    height: 20px;
    margin-bottom: 4px;
    border-radius: 4px;
  }

  &.is-animated .el-skeleton__item {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400% 100%;
    animation: skeleton-loading 1.5s ease infinite;
  }
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
.record-dialog {
  margin: 3px;
}
.activity-table {
  ::v-deep .el-table__body-wrapper {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e0;
      border-radius: 4px;

      &:hover {
        background: #a0aec0;
      }
    }
  }
}
</style>
